import { Events } from "./analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Lightweight client-side A/B experiment harness.
 *
 * Assignment is sticky per-device via localStorage and NEVER changes mid-session
 * (critical — re-rolling mid-experiment would contaminate the data).
 *
 * Analytics: each unique variant assignment fires one `experiment_assignment`
 * event (Firebase) AND posts to `/api/experiments/assign` so we can compute
 * live per-variant counts from our own MongoDB without leaving the app.
 */

const STORAGE_KEY_PREFIX = "sib_exp_";
const DEVICE_ID_KEY = "sib_device_id";

function getDeviceId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

function reportAssignment(experiment, variant) {
  // Fire-and-forget — if backend is down we don't want to block the user.
  try {
    fetch(`${API}/experiments/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ experiment, variant, device_id: getDeviceId() }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

function readOrAssign(key, variants) {
  if (typeof window === "undefined") return variants[0];
  const storageKey = STORAGE_KEY_PREFIX + key;
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing && variants.includes(existing)) return existing;
    const chosen = variants[Math.floor(Math.random() * variants.length)];
    localStorage.setItem(storageKey, chosen);
    // One-time analytics event when a user enters the experiment.
    Events.trackEvent?.("experiment_assignment", { experiment: key, variant: chosen });
    // Mirror to our own server for first-party counts.
    reportAssignment(key, chosen);
    return chosen;
  } catch {
    return variants[0];
  }
}

/**
 * Teaser-density experiment:
 *   - "full"    → render all 4 pro teasers (impact forecast, red-flag,
 *                 locked rewrite cards, +N more issues chip)
 *   - "minimal" → render only the Impact Forecast card
 *
 * Run this for 2+ weeks post-launch to decide which density converts better
 * on the Reserve / Upgrade funnel.
 */
export function getTeaserVariant() {
  return readOrAssign("teaser_density_v1", ["full", "minimal"]);
}

/**
 * Log a variant-tagged client event to our first-party store. Used for
 * conversion-rate tracking on the debug page without waiting on Firebase.
 */
export function trackExperimentEvent(experiment, eventName) {
  if (typeof window === "undefined") return;
  try {
    const variant = localStorage.getItem(STORAGE_KEY_PREFIX + experiment);
    if (!variant) return; // user not in this experiment yet
    fetch(`${API}/experiments/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        experiment,
        variant,
        device_id: getDeviceId(),
        event_name: eventName,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}
